import { useCallback, useEffect, useRef, useState } from "react";
import frameImg from "./JJ26-frame.png";
import "./App.css";
import "./Teko/stylesheet.css";

/** Scale preview CSS size to fit preview container while preserving frame aspect ratio (contain). */
function computeContainDisplaySize(availW, availH, frameW, frameH) {
    if (!availW || !availH || !frameW || !frameH) {
        return { width: 0, height: 0 };
    }
    const frameAspect = frameW / frameH;
    const containerAspect = availW / availH;
    let width;
    let height;
    if (containerAspect > frameAspect) {
        height = availH;
        width = height * frameAspect;
    } else {
        width = availW;
        height = width / frameAspect;
    }
    return { width, height };
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function computeCoverFit(canvasW, canvasH, imgW, imgH) {
    const coverScale = Math.max(canvasW / imgW, canvasH / imgH);
    return {
        coverScale,
        baseWidth: imgW * coverScale,
        baseHeight: imgH * coverScale,
    };
}

function clampOffsets(canvasW, canvasH, drawW, drawH, offsetX, offsetY) {
    const minOffsetX = canvasW - drawW;
    const maxOffsetX = 0;
    const minOffsetY = canvasH - drawH;
    const maxOffsetY = 0;
    return {
        offsetX: clamp(offsetX, minOffsetX, maxOffsetX),
        offsetY: clamp(offsetY, minOffsetY, maxOffsetY),
    };
}

function getCanvasCoords(canvas, clientX, clientY) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
    };
}

function touchDistance(t0, t1) {
    const dx = t0.clientX - t1.clientX;
    const dy = t0.clientY - t1.clientY;
    return Math.hypot(dx, dy);
}

function isIOSLike() {
    return (
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
}

function canvasToPngBlob(canvas) {
    const dataUrl = canvas.toDataURL("image/png");
    const base64 = dataUrl.split(",")[1] ?? "";
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: "image/png" });
}

function App() {
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const previewRef = useRef(null);
    const frameReadyRef = useRef(false);
    const uploadRef = useRef(null);

    const userImageRef = useRef(null);
    const frameImageRef = useRef(null);
    const userObjectUrlRef = useRef(null);

    const zoomRef = useRef(1);
    const offsetXRef = useRef(0);
    const offsetYRef = useRef(0);

    const dragRef = useRef({
        isDragging: false,
        startMouseX: 0,
        startMouseY: 0,
        startOffsetX: 0,
        startOffsetY: 0,
    });

    const pinchRef = useRef({
        active: false,
        lastDistance: 0,
    });

    const [uploadedFile, setUploadedFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    const setZoomState = useCallback((next) => {
        zoomRef.current = next;
    }, []);

    const renderCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        if (!canvas || !ctx) {
            return;
        }

        const cw = canvas.width;
        const ch = canvas.height;
        ctx.clearRect(0, 0, cw, ch);

        const userImg = userImageRef.current;
        if (userImg && userImg.complete && userImg.naturalWidth) {
            const z = zoomRef.current;
            const { baseWidth, baseHeight } = computeCoverFit(
                cw,
                ch,
                userImg.naturalWidth,
                userImg.naturalHeight,
            );
            const drawW = baseWidth * z;
            const drawH = baseHeight * z;
            ctx.drawImage(
                userImg,
                offsetXRef.current,
                offsetYRef.current,
                drawW,
                drawH,
            );
        }

        const frame = frameImageRef.current;
        if (frame && frame.complete && frame.naturalWidth) {
            ctx.drawImage(frame, 0, 0, cw, ch);
        }
    }, []);

    const applyZoomAtCanvasPoint = useCallback(
        (canvasX, canvasY, newZoom) => {
            const canvas = canvasRef.current;
            const userImg = userImageRef.current;
            if (!canvas || !userImg || !userImg.naturalWidth) {
                return;
            }

            const cw = canvas.width;
            const ch = canvas.height;
            const { baseWidth, baseHeight } = computeCoverFit(
                cw,
                ch,
                userImg.naturalWidth,
                userImg.naturalHeight,
            );

            const oldZoom = zoomRef.current;
            const oldDrawW = baseWidth * oldZoom;
            const oldDrawH = baseHeight * oldZoom;
            const newDrawW = baseWidth * newZoom;
            const newDrawH = baseHeight * newZoom;

            if (oldDrawW <= 0 || oldDrawH <= 0) {
                return;
            }

            const fracX = (canvasX - offsetXRef.current) / oldDrawW;
            const fracY = (canvasY - offsetYRef.current) / oldDrawH;

            offsetXRef.current = canvasX - fracX * newDrawW;
            offsetYRef.current = canvasY - fracY * newDrawH;

            const clamped = clampOffsets(
                cw,
                ch,
                newDrawW,
                newDrawH,
                offsetXRef.current,
                offsetYRef.current,
            );
            offsetXRef.current = clamped.offsetX;
            offsetYRef.current = clamped.offsetY;

            setZoomState(newZoom);
        },
        [setZoomState],
    );

    const handleResize = useCallback(function handleResize() {
        const canvas = canvasRef.current;
        const preview = previewRef.current;
        if (!canvas || !preview || !frameReadyRef.current) {
            return;
        }

        const availW = preview.clientWidth;
        const availH = preview.clientHeight;
        if (!availW || !availH) {
            return;
        }

        const bw = canvas.width;
        const bh = canvas.height;
        if (!bw || !bh) {
            return;
        }

        const { width, height } = computeContainDisplaySize(
            availW,
            availH,
            bw,
            bh,
        );
        if (!width || !height) {
            return;
        }

        canvas.style.width = `${Math.round(width)}px`;
        canvas.style.height = `${Math.round(height)}px`;
    }, []);

    useEffect(() => {
        const preview = previewRef.current;
        if (!preview) {
            return undefined;
        }

        handleResize();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", handleResize);
            return () => window.removeEventListener("resize", handleResize);
        }

        const ro = new ResizeObserver(() => {
            handleResize();
        });
        ro.observe(preview);

        return () => {
            ro.disconnect();
        };
    }, [handleResize]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        ctxRef.current = canvas.getContext("2d");
        const img = new Image();
        img.onload = () => {
            frameImageRef.current = img;
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            frameReadyRef.current = true;

            const userImg = userImageRef.current;
            if (userImg && userImg.complete && userImg.naturalWidth) {
                zoomRef.current = 1;
                setZoomState(1);
                const { baseWidth, baseHeight } = computeCoverFit(
                    canvas.width,
                    canvas.height,
                    userImg.naturalWidth,
                    userImg.naturalHeight,
                );
                const clamped = clampOffsets(
                    canvas.width,
                    canvas.height,
                    baseWidth,
                    baseHeight,
                    (canvas.width - baseWidth) / 2,
                    (canvas.height - baseHeight) / 2,
                );
                offsetXRef.current = clamped.offsetX;
                offsetYRef.current = clamped.offsetY;
            }

            renderCanvas();
            handleResize();
        };
        img.src = frameImg;
    }, [handleResize, renderCanvas, setZoomState]);

    useEffect(() => {
        if (!uploadedFile) {
            userImageRef.current = null;
            if (userObjectUrlRef.current) {
                URL.revokeObjectURL(userObjectUrlRef.current);
                userObjectUrlRef.current = null;
            }
            if (dragRef.current.isDragging) {
                dragRef.current.isDragging = false;
                setIsDragging(false);
            }
            pinchRef.current.active = false;
            renderCanvas();
            return;
        }

        const url = URL.createObjectURL(uploadedFile);
        if (userObjectUrlRef.current) {
            URL.revokeObjectURL(userObjectUrlRef.current);
        }
        userObjectUrlRef.current = url;

        const img = new Image();
        img.onload = () => {
            userImageRef.current = img;
            setZoomState(1);

            const canvas = canvasRef.current;
            if (canvas && canvas.width && canvas.height) {
                const { baseWidth, baseHeight } = computeCoverFit(
                    canvas.width,
                    canvas.height,
                    img.naturalWidth,
                    img.naturalHeight,
                );
                const clamped = clampOffsets(
                    canvas.width,
                    canvas.height,
                    baseWidth,
                    baseHeight,
                    (canvas.width - baseWidth) / 2,
                    (canvas.height - baseHeight) / 2,
                );
                offsetXRef.current = clamped.offsetX;
                offsetYRef.current = clamped.offsetY;
            }
            renderCanvas();
        };
        img.src = url;
    }, [uploadedFile, renderCanvas, setZoomState]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const endDrag = () => {
            if (dragRef.current.isDragging) {
                dragRef.current.isDragging = false;
                setIsDragging(false);
            }
        };

        const touchOpts = { passive: false };

        const onTouchStart = (e) => {
            if (!userImageRef.current) {
                return;
            }
            if (e.touches.length === 2) {
                e.preventDefault();
                pinchRef.current.active = true;
                pinchRef.current.lastDistance = touchDistance(
                    e.touches[0],
                    e.touches[1],
                );
                endDrag();
                return;
            }
            if (e.touches.length === 1) {
                pinchRef.current.active = false;
                const t = e.touches[0];
                const { x, y } = getCanvasCoords(canvas, t.clientX, t.clientY);
                dragRef.current = {
                    isDragging: true,
                    startMouseX: x,
                    startMouseY: y,
                    startOffsetX: offsetXRef.current,
                    startOffsetY: offsetYRef.current,
                };
                setIsDragging(true);
            }
        };

        const onTouchMove = (e) => {
            if (!userImageRef.current) {
                return;
            }
            if (e.touches.length >= 2) {
                e.preventDefault();
                const t0 = e.touches[0];
                const t1 = e.touches[1];
                const dist = touchDistance(t0, t1);
                if (!pinchRef.current.active) {
                    pinchRef.current.active = true;
                    pinchRef.current.lastDistance = dist;
                    endDrag();
                } else {
                    const prevDist = pinchRef.current.lastDistance;
                    if (prevDist > 1e-6) {
                        const scale = dist / prevDist;
                        const nextZoom = clamp(
                            zoomRef.current * scale,
                            MIN_ZOOM,
                            MAX_ZOOM,
                        );
                        if (nextZoom !== zoomRef.current) {
                            const mx = (t0.clientX + t1.clientX) / 2;
                            const my = (t0.clientY + t1.clientY) / 2;
                            const { x, y } = getCanvasCoords(canvas, mx, my);
                            applyZoomAtCanvasPoint(x, y, nextZoom);
                        }
                    }
                    pinchRef.current.lastDistance = dist;
                }
                renderCanvas();
                return;
            }
            if (
                dragRef.current.isDragging &&
                e.touches.length === 1 &&
                !pinchRef.current.active
            ) {
                e.preventDefault();
                const userImg = userImageRef.current;
                if (!userImg || !userImg.naturalWidth) {
                    return;
                }
                const t = e.touches[0];
                const { x, y } = getCanvasCoords(canvas, t.clientX, t.clientY);
                const dx = x - dragRef.current.startMouseX;
                const dy = y - dragRef.current.startMouseY;
                const { baseWidth, baseHeight } = computeCoverFit(
                    canvas.width,
                    canvas.height,
                    userImg.naturalWidth,
                    userImg.naturalHeight,
                );
                const drawW = baseWidth * zoomRef.current;
                const drawH = baseHeight * zoomRef.current;
                const clamped = clampOffsets(
                    canvas.width,
                    canvas.height,
                    drawW,
                    drawH,
                    dragRef.current.startOffsetX + dx,
                    dragRef.current.startOffsetY + dy,
                );
                offsetXRef.current = clamped.offsetX;
                offsetYRef.current = clamped.offsetY;
                renderCanvas();
            }
        };

        const onTouchEnd = (e) => {
            if (e.touches.length === 0) {
                pinchRef.current.active = false;
                endDrag();
                return;
            }
            if (e.touches.length === 1) {
                pinchRef.current.active = false;
                if (!userImageRef.current) {
                    return;
                }
                const t = e.touches[0];
                const { x, y } = getCanvasCoords(canvas, t.clientX, t.clientY);
                dragRef.current = {
                    isDragging: true,
                    startMouseX: x,
                    startMouseY: y,
                    startOffsetX: offsetXRef.current,
                    startOffsetY: offsetYRef.current,
                };
                setIsDragging(true);
            }
        };

        const onWheel = (e) => {
            if (!userImageRef.current) {
                return;
            }
            e.preventDefault();
            const { x, y } = getCanvasCoords(canvas, e.clientX, e.clientY);
            const factor = e.deltaY < 0 ? 1.05 : 0.95;
            const nextZoom = clamp(
                zoomRef.current * factor,
                MIN_ZOOM,
                MAX_ZOOM,
            );
            if (nextZoom === zoomRef.current) {
                return;
            }
            applyZoomAtCanvasPoint(x, y, nextZoom);
            renderCanvas();
        };

        const onMouseDown = (e) => {
            if (e.button !== 0) {
                return;
            }
            if (!userImageRef.current) {
                return;
            }
            const { x, y } = getCanvasCoords(canvas, e.clientX, e.clientY);
            dragRef.current = {
                isDragging: true,
                startMouseX: x,
                startMouseY: y,
                startOffsetX: offsetXRef.current,
                startOffsetY: offsetYRef.current,
            };
            setIsDragging(true);
        };

        const onMouseMove = (e) => {
            if (!dragRef.current.isDragging) {
                return;
            }
            const userImg = userImageRef.current;
            if (!userImg || !userImg.naturalWidth) {
                return;
            }
            const { x, y } = getCanvasCoords(canvas, e.clientX, e.clientY);
            const dx = x - dragRef.current.startMouseX;
            const dy = y - dragRef.current.startMouseY;

            const { baseWidth, baseHeight } = computeCoverFit(
                canvas.width,
                canvas.height,
                userImg.naturalWidth,
                userImg.naturalHeight,
            );
            const drawW = baseWidth * zoomRef.current;
            const drawH = baseHeight * zoomRef.current;

            const clamped = clampOffsets(
                canvas.width,
                canvas.height,
                drawW,
                drawH,
                dragRef.current.startOffsetX + dx,
                dragRef.current.startOffsetY + dy,
            );
            offsetXRef.current = clamped.offsetX;
            offsetYRef.current = clamped.offsetY;
            renderCanvas();
        };

        canvas.addEventListener("wheel", onWheel, { passive: false });
        canvas.addEventListener("mousedown", onMouseDown);
        canvas.addEventListener("touchstart", onTouchStart, touchOpts);
        canvas.addEventListener("touchmove", onTouchMove, touchOpts);
        canvas.addEventListener("touchend", onTouchEnd, touchOpts);
        canvas.addEventListener("touchcancel", onTouchEnd, touchOpts);
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", endDrag);

        const onCanvasLeave = () => {
            endDrag();
        };
        canvas.addEventListener("mouseleave", onCanvasLeave);

        return () => {
            canvas.removeEventListener("wheel", onWheel);
            canvas.removeEventListener("mousedown", onMouseDown);
            canvas.removeEventListener("touchstart", onTouchStart, touchOpts);
            canvas.removeEventListener("touchmove", onTouchMove, touchOpts);
            canvas.removeEventListener("touchend", onTouchEnd, touchOpts);
            canvas.removeEventListener("touchcancel", onTouchEnd, touchOpts);
            canvas.removeEventListener("mouseleave", onCanvasLeave);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", endDrag);
        };
    }, [applyZoomAtCanvasPoint, renderCanvas]);

    function handleFileChange(e) {
        const file = e.target.files?.[0];
        setUploadedFile(file ?? null);
    }

    function handleUploadClick() {
        uploadRef.current.click();
    }

    function handleDoneClick() {
        renderCanvas();
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const filename = "june-jam-profile.png";

        if (isIOSLike()) {
            let blob;
            try {
                blob = canvasToPngBlob(canvas);
            } catch {
                return;
            }

            const file = new File([blob], filename, { type: "image/png" });
            const objectUrl = URL.createObjectURL(blob);
            const revokeLater = () =>
                setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);

            const canShareFiles =
                typeof navigator.share === "function" &&
                typeof navigator.canShare === "function" &&
                navigator.canShare({ files: [file] });

            if (canShareFiles) {
                navigator
                    .share({ files: [file] })
                    .then(revokeLater)
                    .catch((err) => {
                        if (err?.name === "AbortError") {
                            URL.revokeObjectURL(objectUrl);
                            return;
                        }
                        const opened = window.open(
                            objectUrl,
                            "_blank",
                            "noopener,noreferrer",
                        );
                        if (opened) {
                            revokeLater();
                        } else {
                            window.location.assign(objectUrl);
                        }
                    });
                return;
            }

            const opened = window.open(
                objectUrl,
                "_blank",
                "noopener,noreferrer",
            );
            if (opened) {
                revokeLater();
            } else {
                window.location.assign(objectUrl);
            }
            return;
        }

        canvas.toBlob((blob) => {
            if (!blob) {
                return;
            }

            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.rel = "noopener";
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 2000);
        }, "image/png");
    }

    const canvasClassName = [
        "canvas",
        uploadedFile && "canvas--editable",
        isDragging && "canvas--dragging",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className="App">
            <div className="preview-area" ref={previewRef}>
                <canvas ref={canvasRef} className={canvasClassName} />
            </div>
            <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                ref={uploadRef}
            />
            <div className="buttons">
                <button type="button" onClick={handleUploadClick}>
                    {uploadedFile
                        ? "Change Profile Image"
                        : "Upload Profile Image"}
                </button>
                {uploadedFile && (
                    <button type="button" onClick={handleDoneClick}>
                        Done / Export
                    </button>
                )}
            </div>
        </div>
    );
}

export default App;
